// Edit-modal section to assign / move / re-role / remove a user's single investor.
// Applied with its own button (independent of the user-fields save) after the
// admin confirms any warning. Blocking rules are also enforced by the backend.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '@startus/lib';
import { useInvestors } from '../hooks/useInvestors';
import { useUserInvestorMembership } from '../hooks/useUserInvestorMembership';
import { investorsService } from '../services/investors.service';
import {
  INVESTOR_MEMBER_ROLES,
  type InvestorMemberRole,
  type SetUserInvestorMembershipResult,
  type UserInvestorMembership,
} from '../types/investor';
import {
  describeMembershipChange,
  type MembershipSelection,
  type MembershipWarning,
} from '../utils/membershipChange';

interface InvestorMembershipSectionProps {
  userId: string;
  /** Called after a successful change; `userDeleted` means the modal should close. */
  onApplied: (result: SetUserInvestorMembershipResult) => void;
}

/** Loads the membership context, then renders the editor for it. */
const InvestorMembershipSection: React.FC<InvestorMembershipSectionProps> = ({
  userId,
  onApplied,
}) => {
  const { t } = useTranslation();
  const { context, loading, error } = useUserInvestorMembership(userId);
  // Latest state returned by the backend after an apply (replaces the loaded one).
  const [applied, setApplied] = useState<UserInvestorMembership | null>(null);
  const current = applied ?? context;

  return (
    <section
      className="border border-base-300 rounded-lg p-4 mb-4"
      aria-labelledby="membership-title"
    >
      <h4 id="membership-title" className="font-semibold mb-3">
        {t('admin:users.membership.title')}
      </h4>
      {loading ? (
        <span className="loading loading-spinner loading-sm" />
      ) : error || !current ? (
        <div className="text-error text-sm" role="alert">
          {getErrorMessage(error, t)}
        </div>
      ) : (
        <>
          {applied && (
            <div className="alert alert-success text-sm mb-3" role="status">
              {t('admin:users.membership.applied')}
            </div>
          )}
          <MembershipEditor
            // Remount from the fresh state after each apply so selection resets.
            key={`${current.membership?.investorId ?? 'none'}:${current.membership?.role ?? ''}`}
            context={current}
            onApplied={(result) => {
              if (result.membership) setApplied(result.membership);
              onApplied(result);
            }}
          />
        </>
      )}
    </section>
  );
};

interface MembershipEditorProps {
  context: UserInvestorMembership;
  onApplied: (result: SetUserInvestorMembershipResult) => void;
}

const MembershipEditor: React.FC<MembershipEditorProps> = ({ context, onApplied }) => {
  const { t } = useTranslation();
  const { investors, loading: investorsLoading } = useInvestors();
  const [selection, setSelection] = useState<MembershipSelection>(() => ({
    investorId: context.membership?.investorId ?? '',
    role: context.membership?.role ?? '',
  }));
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const change = describeMembershipChange(context, selection);
  const needsRole = !!selection.investorId && !selection.role;
  const needsConfirm = change.warnings.length > 0;
  const canApply =
    change.kind !== 'none' &&
    !change.blockedByLastSuperadmin &&
    !needsRole &&
    (!needsConfirm || confirmed) &&
    !submitting;

  const updateSelection = (next: Partial<MembershipSelection>) => {
    setSelection((prev) => ({ ...prev, ...next }));
    // A different change needs a fresh confirmation.
    setConfirmed(false);
    setApplyError(null);
  };

  const handleApply = async () => {
    setSubmitting(true);
    setApplyError(null);
    try {
      const result = await investorsService.setUserMembership(
        context.userId,
        selection.investorId && selection.role
          ? { investorId: selection.investorId, role: selection.role }
          : { investorId: null }
      );
      onApplied(result);
    } catch (err: unknown) {
      // Expected 409s (last SUPERADMIN, multi-investor) come back translated.
      setApplyError(getErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const currentLabel = context.membership
    ? t('admin:users.membership.current', {
        investor: context.membership.investorName,
        role: t(`admin:users.hub.roles.${context.membership.role}`),
      })
    : t('admin:users.membership.currentNone');

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-base-content/70">{currentLabel}</p>

      <div className="grid grid-cols-[auto_1fr] items-center gap-3">
        <label htmlFor="membership-investor" className="label-text">
          {t('admin:users.hub.investor')}
        </label>
        <select
          id="membership-investor"
          className="select select-bordered w-full"
          value={selection.investorId}
          onChange={(e) => updateSelection({ investorId: e.target.value })}
          disabled={investorsLoading || submitting}
        >
          <option value="">{t('admin:users.membership.none')}</option>
          {investors.map((inv) => (
            <option key={inv.id} value={inv.id}>
              {inv.name}
            </option>
          ))}
        </select>

        <label htmlFor="membership-role" className="label-text">
          {t('admin:users.hub.role')}
        </label>
        <select
          id="membership-role"
          className="select select-bordered w-full"
          value={selection.role}
          onChange={(e) => updateSelection({ role: e.target.value as InvestorMemberRole })}
          disabled={!selection.investorId || submitting}
        >
          <option value="" disabled>
            {t('admin:users.hub.selectRole')}
          </option>
          {INVESTOR_MEMBER_ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`admin:users.hub.roles.${r}`)}
            </option>
          ))}
        </select>
      </div>

      {change.blockedByLastSuperadmin && context.membership && (
        <div className="alert alert-error text-sm" role="alert">
          {t('admin:users.membership.blockedLastSuperadmin', {
            investor: context.membership.investorName,
          })}
        </div>
      )}

      {!change.blockedByLastSuperadmin && needsConfirm && (
        <div className="alert alert-warning text-sm flex flex-col items-start gap-2" role="alert">
          <ul className="list-disc pl-5 flex flex-col gap-1">
            {change.warnings.map((w) => (
              <li key={w}>{warningText(w, context, t)}</li>
            ))}
          </ul>
          {change.warnings.includes('userWillBeDeleted') && context.companies.length > 0 && (
            <div>
              <p className="font-semibold">{t('admin:users.membership.companiesTitle')}</p>
              <ul className="list-disc pl-5">
                {context.companies.map((c) => (
                  <li key={c.id}>
                    {c.name} ({c.role})
                  </li>
                ))}
              </ul>
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              // Solid box + strong border: the default outline is near-invisible on alert-warning.
              className="checkbox checkbox-sm checkbox-neutral bg-base-100 border-2 border-base-content/70"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>{t('admin:users.membership.confirm')}</span>
          </label>
        </div>
      )}

      {applyError && (
        <div className="alert alert-error text-sm" role="alert">
          {applyError}
        </div>
      )}

      <div className="flex justify-end">
        {/* type="button": must not submit the surrounding user form. */}
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={handleApply}
          disabled={!canApply}
        >
          {submitting && <span className="loading loading-spinner loading-sm" />}
          {t('admin:users.membership.apply')}
        </button>
      </div>
    </div>
  );
};

// Translated text for one warning; interpolates the current investor where relevant.
function warningText(
  warning: MembershipWarning,
  context: UserInvestorMembership,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  switch (warning) {
    case 'loseWebAccess':
      return t('admin:users.membership.warnings.loseWebAccess');
    case 'loseCurrentInvestor':
      return t('admin:users.membership.warnings.loseCurrentInvestor', {
        investor: context.membership?.investorName ?? '',
      });
    case 'userWillBeDeleted':
      return t('admin:users.membership.warnings.userWillBeDeleted');
  }
}

export default InvestorMembershipSection;
